import { DatePicker, Select, Form } from "antd";
import moment, { Moment } from "moment";
import * as React from "react";
import Entry from "../Model/Entry";
import { ColumnDescription } from "../Model/Column";

const RangePicker = DatePicker.RangePicker;
const Option = Select.Option;

/** A map of column type to a form element, used to dynamically render the FilterForm */
const formItemRenderers: Record<
  string,
  (col: ColumnDescription) => JSX.Element
> = {
  dropdown: (col: ColumnDescription) => (
    <Select
      mode="multiple"
      allowClear={true}
      placeholder={
        col.options !== undefined ? getSelectPlaceholder(col.options) : ""
      }
    >
      {col.options !== undefined &&
        col.options.map((opt) => (
          <Option value={opt} key={opt}>
            {opt}
          </Option>
        ))}
    </Select>
  ),
  // TODO Don't duplicate this
  enum: (col: ColumnDescription) => (
    <Select
      mode="multiple"
      allowClear={true}
      placeholder={
        col.options !== undefined ? getSelectPlaceholder(col.options) : ""
      }
    >
      {col.options !== undefined &&
        col.options.map((opt) => (
          <Option value={opt} key={opt}>
            {opt}
          </Option>
        ))}
    </Select>
  ),
  "bool-dropdown": (col: ColumnDescription) => (
    <Select allowClear={true}>
      {col.options !== undefined && (
        <Option value="true">{col.options[0]}</Option>
      )}
      {col.options !== undefined && (
        <Option value="false">{col.options[1]}</Option>
      )}
    </Select>
  ),
  checkbox: (col: ColumnDescription) => (
    <Select allowClear={true}>
      <Option value="true">Yes</Option>
      <Option value="false">No</Option>
    </Select>
  ),
  "date-range": (col: ColumnDescription) => (
    <RangePicker disabledDate={disableFutureDates} />
  ),
};

function columnToForm(key: string, column: ColumnDescription) {
  // Render children
  let children;

  if (column.children) {
    children = Object.entries(column.children)
      .filter(([key, child]) => child.filterable)
      .filter(([key, child]) => formItemRenderers[child.type] !== undefined)
      .map(([key, child]) => (
        <Form.Item key={key} name={key} label={child.label}>
          {formItemRenderers[child.type](child)}
        </Form.Item>
      ));
  }

  let parent;
  if (column.filterable && formItemRenderers[column.type] !== undefined) {
    parent = (
      <Form.Item key={`${key}_parent`} name={key} label={column.label}>
        {formItemRenderers[column.type](column)}
      </Form.Item>
    );
  }
  return (
    <>
      {parent}
      {children}
    </>
  );
}

export interface FilterFormProps {
  /** Columns to render as form fields */
  columns: Record<string, ColumnDescription>;

  /** Current values */
  values: Entry;

  /** Callback on every change */
  onChange?(fields: Entry): void;
}

export const FilterForm: React.FunctionComponent<FilterFormProps> = (props) => {
  // Determine initial values from props
  const initialValues: { [key: string]: any } = {};
  Object.entries(props.columns)
    .filter(([key, col]) => col.filterable)
    .forEach(([key, col]) => {
      initialValues[key] = props.values[key];
    });

  // On form change callback
  const onChange = (
    changedValues: Record<string, any>,
    values: Record<string, any>
  ) => {
    if (props.onChange !== undefined) {
      // Convert antd fields to Entry
      const entry: Entry = { ...values };

      props.onChange(entry);
    }
  };

  return (
    <Form
      layout="vertical"
      initialValues={initialValues}
      onValuesChange={onChange}
    >
      {Object.entries(props.columns).map(([key, col]) =>
        columnToForm(key, col)
      )}
    </Form>
  );
};

/**
 * Given the options of a dropdown menu, returns the placeholder string.
 * The placeholder string contains a couple of options, comma separated
 * @param options Options of dropdown menu
 */
function getSelectPlaceholder(options: string[]) {
  switch (options.length) {
    case 0:
      return "";
    case 1:
      return options[0];
    case 2:
      return `${options[0]}, ${options[1]}`;
    case 3:
      return `${options[0]}, ${options[1]}, ${options[2]}`;
    default:
      return `${options[0]}, ${options[1]}, ${options[2]}, ...`; // more than 3
  }
}

/**
 * Returns false for every given date that is after today.
 * Use with a DatePicker to only enable dates in the past.
 * @param m Tested date
 * @see DatePicker
 */
function disableFutureDates(m: Moment | null) {
  if (m === null) {
    return true;
  }
  return m.isAfter(moment());
}
